"use client";

import { useTranslations } from "next-intl";
import { Link, usePathname } from "@/i18n/navigation";
import Image from "next/image";
import { COURSES_PUBLIC_ENABLED } from "@/lib/feature-flags";
import { CONTACT_EMAIL, CONTACT_MAILTO, WHATSAPP_URL } from "@/lib/website/contact";

export function Footer({
  locale,
  crNumber,
  showCr = true,
}: {
  locale: string;
  crNumber?: string | null;
  showCr?: boolean;
}) {
  const t = useTranslations("website.footer");
  const pathname = usePathname();

  const exploreLinks: { href: string; label: string }[] = [
    { href: "/", label: t("links.home") },
    ...(COURSES_PUBLIC_ENABLED ? [
      { href: "/courses", label: t("links.courses") },
      { href: "/mentors", label: t("links.instructors") },
    ] : []),
    { href: "/about", label: t("links.about") },
    { href: `/${locale}#contact`, label: t("links.contact") },
  ];
  const isHash = (href: string) => href.includes("#");

  const legalLinks = [
    { href: "/legal/terms", label: t("links.terms") },
    { href: "/legal/privacy", label: t("links.privacy") },
    { href: "/legal/cookies", label: t("links.cookies") },
  ];

  const otherLocale = locale === "en" ? "ar" : "en";

  return (
    <footer className="border-t border-(--fm-border) bg-(--fm-background)">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-3 mb-2">
              <Image src="/logo.png" alt="FortMind" width={64} height={64} className="shrink-0" />
              <div className="flex flex-col">
                <span className="font-heading text-2xl font-bold tracking-widest uppercase leading-tight">
                  <span className="text-(--fm-text-primary)">{t("fort")}</span>
                  <span className="text-(--fm-accent)">{t("mind")}</span>
                </span>
                <span className="text-2xs text-(--fm-text-secondary) tracking-wider uppercase leading-tight">
                  {t("tagline")}
                </span>
              </div>
            </div>
            {/* Social icons */}
            <div className="mt-4 flex items-center gap-3">
              {/* LinkedIn */}
              <a href="https://linkedin.com/company/fortmindqa" target="_blank" rel="noopener noreferrer" className="text-(--fm-text-secondary) hover:text-(--fm-accent) transition-colors" aria-label="LinkedIn">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
              </a>
              {/* X (Twitter) */}
              <a href="https://x.com/fortmindqa" target="_blank" rel="noopener noreferrer" className="text-(--fm-text-secondary) hover:text-(--fm-accent) transition-colors" aria-label="X (Twitter)">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
              </a>
              {/* WhatsApp */}
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="text-(--fm-text-secondary) hover:text-(--fm-accent) transition-colors" aria-label="WhatsApp">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
              </a>
              {/* Instagram */}
              <a href="https://instagram.com/fortmindqa" target="_blank" rel="noopener noreferrer" className="text-(--fm-text-secondary) hover:text-(--fm-accent) transition-colors" aria-label="Instagram">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M7.75 2C4.574 2 2 4.574 2 7.75v8.5C2 19.426 4.574 22 7.75 22h8.5C19.426 22 22 19.426 22 16.25v-8.5C22 4.574 19.426 2 16.25 2h-8.5zM20.5 7.75v8.5a4.25 4.25 0 01-4.25 4.25h-8.5a4.25 4.25 0 01-4.25-4.25v-8.5A4.25 4.25 0 017.75 3.5h8.5a4.25 4.25 0 014.25 4.25zM12 7a5 5 0 100 10 5 5 0 000-10zm0 1.5a3.5 3.5 0 110 7 3.5 3.5 0 010-7zm5.25-2.4a1.15 1.15 0 100 2.3 1.15 1.15 0 000-2.3z" /></svg>
              </a>

            </div>
          </div>
          {/* Explore */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-(--fm-text-primary) mb-3">
              {t("exploreHeading")}
            </h3>
            <ul className="space-y-2">
              {exploreLinks.map((link) => (
                <li key={link.href}>
                  {isHash(link.href) ? (
                    <a
                      href={link.href}
                      className="text-sm text-(--fm-text-secondary) hover:text-(--fm-text-primary) transition-colors"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      href={link.href}
                      className="text-sm text-(--fm-text-secondary) hover:text-(--fm-text-primary) transition-colors"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-(--fm-text-primary) mb-3">
              {t("legalHeading")}
            </h3>
            <ul className="space-y-2">
              {legalLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-(--fm-text-secondary) hover:text-(--fm-text-primary) transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact + Language */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-(--fm-text-primary) mb-3">
              {t("contactHeading")}
            </h3>
            <a
              href={CONTACT_MAILTO}
              className="block text-sm text-(--fm-text-secondary) hover:text-(--fm-text-primary) transition-colors"
            >
              {CONTACT_EMAIL}
            </a>
            <p className="text-sm text-(--fm-text-secondary) mt-1">{t("location")}</p>
            <Link
              href={pathname}
              locale={otherLocale}
              className="inline-block mt-4 text-sm text-(--fm-text-secondary) hover:text-(--fm-text-primary) transition-colors"
              aria-label={locale === "en" ? "Switch to Arabic" : "Switch to English"}
            >
              {locale === "en" ? "العربية" : "English"}
            </Link>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-(--fm-border) flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
          <p className="text-xs text-(--fm-text-secondary)">
            {t("copyright", { year: new Date().getFullYear() })}
          </p>
          {showCr && crNumber && (
            <>
              <span className="hidden sm:inline text-(--fm-text-secondary)/40 text-xs">·</span>
              <p className="text-xs text-(--fm-text-secondary)">
                {t("cr", { number: crNumber })}
              </p>
            </>
          )}
        </div>
      </div>
    </footer>
  );
}
